class KoketaSocialReels extends HTMLElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('.koketa-social-reel__video')];
    if (!this.videos.length) return;

    this.visibilityRatios = new Map();
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.saveData = Boolean(navigator.connection?.saveData);
    this.canObserve = 'IntersectionObserver' in window;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);

    this.videos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.pause();
    });

    this.setStaticState();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.motionPreference.addEventListener) {
      this.motionPreference.addEventListener('change', this.handleMotionChange);
    } else {
      this.motionPreference.addListener(this.handleMotionChange);
    }

    if (this.canObserve) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            this.visibilityRatios.set(video, entry.isIntersecting ? entry.intersectionRatio : 0);
          });
          this.syncAll();
        },
        { threshold: [0, 0.35, 0.65] }
      );

      this.videos.forEach((video) => this.observer.observe(video));
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.pauseAll();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (!this.motionPreference) return;

    if (this.motionPreference.removeEventListener) {
      this.motionPreference.removeEventListener('change', this.handleMotionChange);
    } else {
      this.motionPreference.removeListener(this.handleMotionChange);
    }
  }

  shouldPlay(video, activeVideo) {
    return (
      video === activeVideo &&
      (this.visibilityRatios.get(video) || 0) >= 0.35 &&
      !this.motionPreference.matches &&
      !this.saveData &&
      !document.hidden
    );
  }

  syncVideo(video, activeVideo) {
    const card = video.closest('.koketa-social-reel');

    if (!this.shouldPlay(video, activeVideo)) {
      video.pause();
      card?.classList.remove('is-video-playing');
      return;
    }

    const playPromise = video.play();
    if (playPromise?.then) {
      playPromise
        .then(() => card?.classList.add('is-video-playing'))
        .catch(() => card?.classList.remove('is-video-playing'));
    }
  }

  syncAll() {
    const activeVideo = this.videos.reduce((best, video) => {
      if (!best) return video;
      return (this.visibilityRatios.get(video) || 0) > (this.visibilityRatios.get(best) || 0) ? video : best;
    }, null);
    this.videos.forEach((video) => this.syncVideo(video, activeVideo));
  }

  pauseAll() {
    this.videos?.forEach((video) => {
      video.pause();
      video.closest('.koketa-social-reel')?.classList.remove('is-video-playing');
    });
  }

  setStaticState() {
    const isStatic = this.motionPreference.matches || this.saveData || !this.canObserve;
    this.classList.toggle('is-static', isStatic);

    this.videos.forEach((video) => {
      // Reproduction is driven only by IntersectionObserver so a video can
      // never start before the card is actually visible.
      video.autoplay = false;
      if (isStatic) video.pause();
    });
  }

  handleVisibilityChange() {
    this.syncAll();
  }

  handleMotionChange() {
    this.setStaticState();
    this.syncAll();
  }
}

if (!customElements.get('koketa-social-reels')) {
  customElements.define('koketa-social-reels', KoketaSocialReels);
}
